import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadCoursesByParticipantId } from '../../../../store/courseService';
import {
    loadDocumentTypeIdsAndNamesForContract,
    loadDocumentTypesForContractId
} from '../../../../store/directusService';
import {
    loadActionPlans,
    loadAssetRequests,
    loadFinancialRequests,
    loadParticipantBarriers,
    loadParticipantJobGoals,
    loadQuestionnaireAnswersByParticipant,
    loadQuestionnaires
} from '../../../../store/participantService';
import {
    loadVacancySubmissionsByParticipantId,
    searchVacanciesByServiceId
} from '../../../../store/recruitmentService';
import { loadUsersByServiceIds } from '../../../../store/userService';

import GenerateAppointmentPDF from './GenerateAppointmentPDF';
import PDFContentManagement from './PDFContentManagement';


const AppointmentPDFManagement = ({
    onSavePDF,
    fileName,
    appointmentType,
    participantsActiveAccordions,
    adviserName,
    currentParticipant,
    participantSignature,
    adviserSignature,
    attendanceId,
    currentAppointment,
    appointment,
    nextAppointment,
    signatureDateTime,
    exitFormTemplateId,
    doubtFormId,
    complianceActivityFormQuestionnaireId
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [isLoaded, setIsLoaded] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const participantsCourses = useSelector(
        (state) => state.entities.courseService.participantsCourses
    );
    const participantsCoursesMetaData = useSelector(
        (state) => state.entities.courseService.participantsCoursesMetaData
    );
    const vacanciesForServices = useSelector(
        (state) => state.entities.recruitmentService.vacanciesForServices
    );
    const vacanciesForServicesMetaData = useSelector(
        (state) => state.entities.recruitmentService.vacanciesForServicesMetaData
    );

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadDocumentTypesForContractId(currentParticipant.contractId));
        dispatch(loadDocumentTypeIdsAndNamesForContract(currentParticipant.contractId));
        currentParticipant?.serviceId &&
            dispatch(loadUsersByServiceIds([currentParticipant.serviceId]));

        // barriers or action plans
        if (participantsActiveAccordions.actionPlan || participantsActiveAccordions.barriers) {
            dispatch(loadParticipantBarriers(currentParticipant.id));
        }
        //questionnaires
        if (participantsActiveAccordions.questionnaire) {
            dispatch(loadQuestionnaires());
            dispatch(loadQuestionnaireAnswersByParticipant(currentParticipant.id));
        }
        //action plans
        if (participantsActiveAccordions.actionPlan) {
            dispatch(loadActionPlans(currentParticipant.id));
        }
        //courses
        if (participantsActiveAccordions.courses) {
            dispatch(loadCoursesByParticipantId(currentParticipant.id, 0));
        }
        //job goals
        if (participantsActiveAccordions.jobReadiness)
            dispatch(loadParticipantJobGoals(currentParticipant.id));

        if (participantsActiveAccordions.request) {
            //financial requests
            dispatch(loadFinancialRequests(currentParticipant.id));
            //asset requests
            dispatch(loadAssetRequests(currentParticipant.id));
        }
        //vacancies
        if (participantsActiveAccordions.vacancy) {
            dispatch(loadVacancySubmissionsByParticipantId(currentParticipant.id));
            dispatch(
                searchVacanciesByServiceId(
                    { inactive: false, serviceIds: loggedInUser.serviceIds },
                    0
                )
            );
        }
    }, []);

    useEffect(() => {
        if (
            participantsCourses.length > 0 &&
            'number' in participantsCoursesMetaData &&
            !participantsCoursesMetaData.last
        ) {
            dispatch(
                loadCoursesByParticipantId(
                    currentParticipant?.id,
                    participantsCoursesMetaData.number + 1
                )
            );
        }
    }, [participantsCoursesMetaData, participantsCourses]);

    useEffect(() => {
        if (
            vacanciesForServices.length > 0 &&
            'number' in vacanciesForServicesMetaData &&
            !vacanciesForServicesMetaData.last
        )
            dispatch(
                searchVacanciesByServiceId(
                    { inactive: false, serviceIds: loggedInUser.serviceIds },
                    vacanciesForServicesMetaData.number + 1
                )
            );
    }, [vacanciesForServices, vacanciesForServicesMetaData]);

    // CALLBACKS
    const onIsLoaded = () => setIsLoaded(true);

    // RENDER
    return (
        <div>
            <GenerateAppointmentPDF
                onSavePDF={onSavePDF}
                loggedInUserId={loggedInUser.id}
                currentParticipantId={currentParticipant.id}
                fileName={fileName}
                isLoaded={isLoaded}
                appointmentType={appointmentType}
                mandatoryAppointment={appointment.mandatory}
                appointmentLocationId={appointment.locationId}
                exitFormTemplateId={exitFormTemplateId}
                doubtFormId={doubtFormId}
                complianceActivityFormQuestionnaireId={complianceActivityFormQuestionnaireId}
                attendanceId={attendanceId}
                currentAppointment={currentAppointment}>
                <PDFContentManagement
                    adviserName={adviserName}
                    currentParticipant={currentParticipant}
                    participantSignature={participantSignature}
                    adviserSignature={adviserSignature}
                    currentAppointment={currentAppointment}
                    appointment={appointment}
                    nextAppointment={nextAppointment}
                    signatureDateTime={signatureDateTime}
                    onIsLoaded={onIsLoaded}
                    participantsActiveAccordions={participantsActiveAccordions}
                />
            </GenerateAppointmentPDF>
        </div>
    );
};

AppointmentPDFManagement.propTypes = {
    adviserName: PropTypes.string,
    currentParticipant: PropTypes.object,
    adviserSignature: PropTypes.string,
    participantSignature: PropTypes.string,
    attendanceId: PropTypes.string,
    currentAppointment: PropTypes.object,
    signatureDateTime: PropTypes.string,
    appointment: PropTypes.object,
    nextAppointment: PropTypes.object,
    onSavePDF: PropTypes.func,
    fileName: PropTypes.string,
    appointmentType: PropTypes.object,
    participantsActiveAccordions: PropTypes.object,
    exitFormTemplateId: PropTypes.string,
    doubtFormId: PropTypes.string,
    complianceActivityFormQuestionnaireId: PropTypes.string
};

export default AppointmentPDFManagement;
